if (!customElements.get('weather-timeline-slider')) {
  class WeatherTimelineSlider extends HTMLElement {
  static selectors = {
    button: '.weather-timeline__button[data-timeline-index]',
    image: '.weather-timeline__image',
    slide: '[data-timeline-slide]',
    slidesTrack: '[data-timeline-slides]',
    navScroller: '[data-timeline-nav-scroller]',
    gallery: '[data-timeline-gallery]',
    stickyWrapper: '[data-timeline-sticky-wrapper]',
    prose: '.section-header .prose',
  };

  constructor() {
    super();

    this.buttons = [];
    this.slides = [];
    this.slidesTrack = null;
    this.navScroller = null;
    this.gallery = null;
    this.stickyWrapper = null;
    this.prose = null;
    this.proseObserver = null;
    this.triggers = [];
    this.activeIndex = 0;
    this.pointerStartX = 0;
    this.pointerDeltaX = 0;
    this.pointerStartSlide = null;
    this.activePointerId = null;
    this.isDragging = false;
    this.isActive = false;
    this.isAnimating = false;
    this.isTimeLocked = false;
    this.timeLockTimer = null;
    this.lastScrollY = 0;
    this.scrollAccumulator = 0;
    this.scrollStep = 520;
    this.scrollThreshold = 160;
    this.initialHold = 160;
    this.lastHold = 520;
    this.switchCooldown = 500;
    this.resizeThrottleMs = 180;
    this.lastResizeHandledAt = 0;
    this.scrollTriggerReadyTimer = null;
    this.scrollTriggerRetryCount = 0;
    this.maxScrollTriggerRetries = 60;
    this.rafId = null;
    this.scrollRafId = null;
    this.abortController = null;

    this.onResize = this.onResize.bind(this);
    this.onNativeScroll = this.onNativeScroll.bind(this);
    this.onScrollTriggerReady = this.onScrollTriggerReady.bind(this);
    this.onPointerDown = this.onPointerDown.bind(this);
    this.onPointerMove = this.onPointerMove.bind(this);
    this.onPointerUp = this.onPointerUp.bind(this);
    this.onPointerCancel = this.onPointerCancel.bind(this);
    this.onPointerCaptureLost = this.onPointerCaptureLost.bind(this);
    this.onPointerLeave = this.onPointerLeave.bind(this);
  }

  connectedCallback() {
    if (this.abortController) return;

    this.buttons = Array.from(this.querySelectorAll(WeatherTimelineSlider.selectors.button));
    this.slides = Array.from(this.querySelectorAll(WeatherTimelineSlider.selectors.slide));
    this.slidesTrack = this.querySelector(WeatherTimelineSlider.selectors.slidesTrack);
    this.navScroller = this.querySelector(WeatherTimelineSlider.selectors.navScroller);
    this.gallery = this.querySelector(WeatherTimelineSlider.selectors.gallery);
    this.stickyWrapper = this.querySelector(WeatherTimelineSlider.selectors.stickyWrapper);
    this.prose = this.querySelector(WeatherTimelineSlider.selectors.prose);

    if (!this.buttons.length || !this.slidesTrack || !this.gallery || !this.navScroller) return;

    this.abortController = new AbortController();
    const { signal } = this.abortController;

    this.buttons.forEach((button) => {
      button.addEventListener('click', () => this.scrollToIndex(Number(button.dataset.timelineIndex)), { signal });
      button.addEventListener('keydown', (event) => this.onButtonKeydown(event), { signal });
    });

    this.slides.forEach((slide) => {
      slide.addEventListener('click', () => {
        const slideIndex = Number(slide.dataset.timelineIndex);
        if (!Number.isNaN(slideIndex) && slideIndex !== this.activeIndex) {
          this.scrollToIndex(slideIndex);
        }
      }, { signal });
    });

    this.querySelectorAll(WeatherTimelineSlider.selectors.image).forEach((image) => {
      image.addEventListener('dragstart', (event) => event.preventDefault(), { signal });
    });

    this.gallery.addEventListener('pointerdown', this.onPointerDown, { signal });
    this.gallery.addEventListener('pointermove', this.onPointerMove, { signal });
    this.gallery.addEventListener('pointerup', this.onPointerUp, { signal });
    this.gallery.addEventListener('pointercancel', this.onPointerCancel, { signal });
    this.gallery.addEventListener('lostpointercapture', this.onPointerCaptureLost, { signal });
    this.gallery.addEventListener('pointerleave', this.onPointerLeave, { signal });
    window.addEventListener('scroll', this.onNativeScroll, { passive: true, signal });
    window.addEventListener('resize', this.onResize, { passive: true, signal });
    window.addEventListener('load', this.onScrollTriggerReady, { once: true, signal });

    this.observeProse();
    this.setupPinnedScroll();
    this.update(0);
  }

  disconnectedCallback() {
    if (!this.abortController) return;

    this.abortController.abort();
    this.abortController = null;

    if (this.rafId) {
      window.cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }

    if (this.scrollRafId) {
      window.cancelAnimationFrame(this.scrollRafId);
      this.scrollRafId = null;
    }

    if (this.proseObserver) {
      this.proseObserver.disconnect();
      this.proseObserver = null;
    }

    if (this.timeLockTimer) {
      window.clearTimeout(this.timeLockTimer);
      this.timeLockTimer = null;
    }

    if (this.scrollTriggerReadyTimer) {
      window.clearTimeout(this.scrollTriggerReadyTimer);
      this.scrollTriggerReadyTimer = null;
    }

    this.destroyPinnedScroll();
  }

  update(index, focusButton = false) {
    const count = this.buttons.length;
    if (!count || !this.slidesTrack || !this.gallery) return;

    this.activeIndex = Math.max(0, Math.min(index, count - 1));
    this.classList.toggle('is-first-active', this.activeIndex === 0);

    const slideWidth = this.getSlideWidth();
    const offset = this.getOffset(this.activeIndex, slideWidth);
    const progress = count === 1 ? 1 : this.activeIndex / (count - 1);

    this.setTimelineProgress(progress);
    this.slidesTrack.style.transform = `translate3d(${-offset}px, 0, 0)`;

    this.buttons.forEach((button, buttonIndex) => {
      const isActive = buttonIndex === this.activeIndex;
      const isComplete = buttonIndex <= this.activeIndex;
      button.setAttribute('aria-selected', String(isActive));
      button.tabIndex = isActive ? 0 : -1;
      button.classList.toggle('is-complete', isComplete);
      if (isActive && focusButton) button.focus();
    });

    this.slides.forEach((slide, slideIndex) => {
      const isActive = slideIndex === this.activeIndex;
      slide.classList.toggle('is-active', isActive);
      slide.setAttribute('aria-hidden', String(!isActive));

      const image = slide.querySelector(WeatherTimelineSlider.selectors.image);
      if (image) {
        image.style.setProperty('--parallax-x', `${(slideIndex - this.activeIndex) * -18}px`);
      }
    });

    this.centerTimelineButton(this.buttons[this.activeIndex]);
  }

  observeProse() {
    if (!this.prose) return;
    const proseItems = Array.from(this.prose.children);
    proseItems.forEach((item, index) => {
      item.style.setProperty('--weather-prose-delay', `${index * 0.18}s`);
    });

    if (!('IntersectionObserver' in window)) {
      this.prose.classList.add('is-visible');
      return;
    }

    this.proseObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          this.prose.classList.add('is-visible');
          this.proseObserver?.disconnect();
          this.proseObserver = null;
        }
      });
    }, {
      rootMargin: '0px 0px -18% 0px',
      threshold: 0.2,
    });

    this.proseObserver.observe(this.prose);
  }

  setupPinnedScroll() {
    this.resetDragState();
    this.destroyPinnedScroll();

    if (this.buttons.length <= 1 || !this.stickyWrapper) return;
    if (window.matchMedia('(max-width: 699px)').matches) return;
    if (!window.ScrollTrigger) {
      this.waitForScrollTrigger();
      return;
    }

    if (this.scrollTriggerReadyTimer) {
      window.clearTimeout(this.scrollTriggerReadyTimer);
      this.scrollTriggerReadyTimer = null;
    }

    this.scrollTriggerRetryCount = 0;
    if (window.gsap?.registerPlugin) {
      window.gsap.registerPlugin(window.ScrollTrigger);
    }

    const cardCount = this.buttons.length - 1;
    const totalDistance = this.initialHold + this.scrollStep * cardCount + this.lastHold;

    const trigger = window.ScrollTrigger.create({
      trigger: this.stickyWrapper,
      start: 'top top',
      end: `+=${totalDistance}`,
      pin: true,
      anticipatePin: 1,
      scrub: false,
      invalidateOnRefresh: true,
      onEnter: (self) => {
        this.activatePinnedScroll(self);
        this.updateFromNativeScroll();
      },
      onEnterBack: (self) => {
        this.activatePinnedScroll(self);
        this.updateFromNativeScroll();
      },
      onLeave: () => this.deactivatePinnedScroll(this.buttons.length - 1),
      onLeaveBack: () => this.deactivatePinnedScroll(0),
    });

    this.triggers.push(trigger);

    window.requestAnimationFrame(() => {
      window.ScrollTrigger?.refresh();
      this.updateFromNativeScroll();
    });
  }

  destroyPinnedScroll() {
    this.triggers.forEach((trigger) => trigger.kill());
    this.triggers.length = 0;
    this.isActive = false;
    this.isAnimating = false;
    this.scrollAccumulator = 0;
  }

  activatePinnedScroll(scrollTrigger) {
    this.isActive = true;
    this.isAnimating = false;
    this.scrollAccumulator = 0;
    this.lastScrollY = scrollTrigger.scroll() - scrollTrigger.start;
    this.isTimeLocked = true;
    window.clearTimeout(this.timeLockTimer);
    this.timeLockTimer = window.setTimeout(() => {
      this.isTimeLocked = false;
    }, 600);
  }

  deactivatePinnedScroll(index) {
    this.isActive = false;
    this.isAnimating = false;
    this.isTimeLocked = false;
    this.scrollAccumulator = 0;
    window.clearTimeout(this.timeLockTimer);
    this.update(index);
  }

  onNativeScroll() {
    if (this.scrollRafId) return;

    this.scrollRafId = window.requestAnimationFrame(() => {
      this.scrollRafId = null;
      this.updateFromNativeScroll();
    });
  }

  updateFromNativeScroll() {
    const scrollTrigger = this.triggers[0];
    if (!scrollTrigger) return;

    const count = this.buttons.length;
    if (count <= 1) return;

    const scrollProgress = this.getNativeScrollProgress(scrollTrigger);
    const nextIndex = this.getIndexFromScrollProgress(scrollProgress);
    const stageProgress = count === 1 ? 1 : nextIndex / (count - 1);

    if (nextIndex !== this.activeIndex) {
      this.update(nextIndex);
    }

    this.setTimelineProgress(stageProgress);
  }

  getNativeScrollProgress(scrollTrigger) {
    const count = this.buttons.length;
    const slideDistance = this.scrollStep * (count - 1);
    if (slideDistance <= 0) return 0;

    const scrollY = window.scrollY || window.pageYOffset || document.documentElement.scrollTop || 0;
    const localScrollY = scrollY - scrollTrigger.start - this.initialHold;

    return Math.max(0, Math.min(1, localScrollY / slideDistance));
  }

  getIndexFromScrollProgress(progress) {
    const count = this.buttons.length;
    if (count <= 1) return 0;

    const stageIndex = Math.floor(Math.max(0, Math.min(1, progress)) * count);

    return Math.max(0, Math.min(count - 1, stageIndex));
  }

  setTimelineProgress(progress) {
    this.style.setProperty('--timeline-progress', Math.max(0, Math.min(1, progress)));
  }

  snapToIndex(index, scrollTrigger) {
    if (!scrollTrigger) return;

    this.isAnimating = true;
    this.update(index);
    scrollTrigger.scroll(this.getScrollYForIndex(index, scrollTrigger));

    window.setTimeout(() => {
      this.isAnimating = false;
      this.scrollAccumulator = 0;
      this.lastScrollY = scrollTrigger.scroll() - scrollTrigger.start;
    }, this.switchCooldown);
  }

  scrollToIndex(index) {
    const count = this.buttons.length;
    if (!count) return;

    const targetIndex = Math.max(0, Math.min(index, count - 1));
    const scrollTrigger = this.triggers[0];
    if (!scrollTrigger) {
      this.update(targetIndex);
      return;
    }

    window.scrollTo({
      top: this.getScrollYForIndex(targetIndex, scrollTrigger),
      behavior: 'smooth',
    });
  }

  getScrollYForIndex(index, scrollTrigger) {
    const count = this.buttons.length;
    if (count <= 1) return scrollTrigger.start + this.initialHold;

    const slideDistance = this.scrollStep * (count - 1);
    const stageOffset = slideDistance * (Math.max(0, Math.min(index, count - 1)) / count);

    return scrollTrigger.start + this.initialHold + stageOffset;
  }

  waitForScrollTrigger() {
    if (this.scrollTriggerReadyTimer || this.scrollTriggerRetryCount >= this.maxScrollTriggerRetries) return;

    this.scrollTriggerReadyTimer = window.setTimeout(() => {
      this.scrollTriggerReadyTimer = null;
      if (!this.abortController) return;

      if (window.ScrollTrigger) {
        this.setupPinnedScroll();
        return;
      }

      this.scrollTriggerRetryCount += 1;
      this.waitForScrollTrigger();
    }, 100);
  }

  onScrollTriggerReady() {
    if (this.triggers.length || !window.ScrollTrigger) return;

    this.setupPinnedScroll();
  }

  getGap() {
    const gap = Number.parseFloat(window.getComputedStyle(this.slidesTrack).columnGap || 0);
    return Number.isNaN(gap) ? 0 : gap;
  }

  getSlideWidth() {
    const currentSlide = this.slides[this.activeIndex] || this.slides[0];
    return currentSlide ? currentSlide.offsetWidth : this.getVisibleWidth();
  }

  getPaddingStart() {
    if (!this.slidesTrack) return 0;
    const value = Number.parseFloat(window.getComputedStyle(this.slidesTrack).paddingInlineStart || '0');
    return Number.isFinite(value) ? value : 0;
  }

  getStyleNumber(element, property) {
    if (!element) return 0;
    const styles = window.getComputedStyle(element);
    const raw = property.startsWith('--') ? styles.getPropertyValue(property) : styles[property];
    const value = Number.parseFloat(raw || '0');
    return Number.isFinite(value) ? value : 0;
  }

  getGalleryStartOffset() {
    return this.getStyleNumber(this.gallery, '--weather-timeline-gallery-start-offset');
  }

  getGalleryPaddingEnd() {
    return this.getStyleNumber(this.gallery, 'paddingInlineEnd');
  }

  getVisibleWidth() {
    if (!this.gallery) return 0;
    const width = this.gallery.getBoundingClientRect().width;
    return Math.max(0, width - this.getGalleryStartOffset() - this.getGalleryPaddingEnd());
  }

  getOffset(index, slideWidth) {
    const targetOffset = index * (slideWidth + this.getGap());
    const visibleWidth = this.getVisibleWidth();
    const paddingStart = this.getPaddingStart();
    const centeredOffset = targetOffset + paddingStart - Math.max(0, (visibleWidth - slideWidth) / 2);
    if (this.isMobileViewport() && index === this.slides.length - 1) {
      return Math.max(0, targetOffset + paddingStart + slideWidth - visibleWidth);
    }

    const lastItemOffset = index === this.slides.length - 1 ? slideWidth : 0;
    const maxOffset = Math.max(0, this.slidesTrack.scrollWidth - visibleWidth + lastItemOffset);

    return Math.min(Math.max(0, centeredOffset), maxOffset);
  }

  centerTimelineButton(button) {
    if (!button || !this.navScroller) return;

    const buttonCenter = button.offsetLeft + button.offsetWidth / 2;
    const targetLeft = buttonCenter - this.navScroller.clientWidth / 2;

    this.navScroller.scrollTo({
      left: Math.max(0, targetLeft),
      behavior: 'smooth',
    });
  }

  onButtonKeydown(event) {
    switch (event.key) {
      case 'ArrowRight':
        event.preventDefault();
        this.scrollToIndex(this.activeIndex + 1);
        break;
      case 'ArrowLeft':
        event.preventDefault();
        this.scrollToIndex(this.activeIndex - 1);
        break;
      case 'Home':
        event.preventDefault();
        this.scrollToIndex(0);
        break;
      case 'End':
        event.preventDefault();
        this.scrollToIndex(this.buttons.length - 1);
        break;
    }
  }

  onPointerDown(event) {
    if (event.isPrimary === false) return;
    if (event.pointerType === 'mouse' && event.button !== 0) return;

    this.pointerStartX = event.clientX;
    this.pointerDeltaX = 0;
    this.pointerStartSlide = event.target.closest?.(WeatherTimelineSlider.selectors.slide) || null;
    this.activePointerId = event.pointerId;
    this.isDragging = true;
    this.gallery.classList.add('is-dragging');
    this.gallery.setPointerCapture(event.pointerId);
  }

  onPointerMove(event) {
    if (!this.isDragging) return;
    if (event.pointerId !== this.activePointerId) return;

    const deltaX = event.clientX - this.pointerStartX;
    if (this.isBlockedMobileBoundaryDrag(deltaX)) {
      event.preventDefault();
      this.pointerDeltaX = 0;
      return;
    }

    this.pointerDeltaX = deltaX;
  }

  onPointerUp(event) {
    if (!this.isDragging) return;
    if (event.pointerId !== this.activePointerId) return;

    const dragDistance = Math.abs(this.pointerDeltaX);
    if (this.gallery.hasPointerCapture(event.pointerId)) {
      this.gallery.releasePointerCapture(event.pointerId);
    }

    if (dragDistance > 40) {
      if (this.isBlockedMobileBoundaryDrag(this.pointerDeltaX)) {
        this.resetDragState();
        return;
      }
      this.scrollToIndex(this.pointerDeltaX < 0 ? this.activeIndex + 1 : this.activeIndex - 1);
      this.resetDragState();
      return;
    }

    if (dragDistance <= 8 && this.pointerStartSlide) {
      const slideIndex = Number(this.pointerStartSlide.dataset.timelineIndex);
      if (!Number.isNaN(slideIndex) && slideIndex !== this.activeIndex) {
        this.scrollToIndex(slideIndex);
      }
    }

    this.resetDragState();
  }

  isMobileViewport() {
    return window.matchMedia('(max-width: 699px)').matches;
  }

  isBlockedMobileBoundaryDrag(deltaX) {
    return this.isMobileViewport() && this.activeIndex === this.slides.length - 1 && deltaX < 0;
  }

  onPointerCancel(event) {
    if (event.pointerId !== this.activePointerId) return;

    if (this.gallery.hasPointerCapture(event.pointerId)) {
      this.gallery.releasePointerCapture(event.pointerId);
    }
    this.resetDragState();
  }

  onPointerCaptureLost() {
    this.resetDragState();
  }

  onPointerLeave(event) {
    if (!this.isDragging || event.pointerId !== this.activePointerId) return;

    if (this.gallery.hasPointerCapture(event.pointerId)) {
      this.gallery.releasePointerCapture(event.pointerId);
    }
    this.resetDragState();
  }

  resetDragState() {
    if (!this.gallery) return;

    this.isDragging = false;
    this.pointerDeltaX = 0;
    this.pointerStartSlide = null;
    this.activePointerId = null;
    this.gallery.classList.remove('is-dragging');
  }

  onResize() {
    const now = Date.now();
    if (now - this.lastResizeHandledAt < this.resizeThrottleMs) return;
    this.lastResizeHandledAt = now;

    if (this.rafId) return;

    this.rafId = window.requestAnimationFrame(() => {
      this.rafId = null;
      this.setupPinnedScroll();
      if (this.triggers.length) {
        this.updateFromNativeScroll();
      } else {
        this.update(this.activeIndex);
      }
    });
  }

}

  customElements.define('weather-timeline-slider', WeatherTimelineSlider);
}
